import { Dispatch, memo, SetStateAction, useCallback, useRef, useState } from 'react';

import { CreateSpeaker, Speaker } from '@/types/speakers.types';
import Container from '@ui/container/container';
import UploadLarge from '@ui/uploads/upload-large';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';

type TUploadCastImageProps = {
  workingSpeaker: Speaker | CreateSpeaker;
  setWorkingSpeaker: Dispatch<SetStateAction<Speaker | CreateSpeaker>>;
};

const UploadCastImage = memo(({ workingSpeaker, setWorkingSpeaker }: TUploadCastImageProps) => {
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [upLoadingImage, setUpLoadingImage] = useState(false);

  const initials = `${workingSpeaker?.first_name?.[0] || ''}${workingSpeaker?.last_name?.[0] || ''}`.trim();

  const handleImageFile = useCallback(
    async (file: File | FileList) => {
      try {
        if (file instanceof File) {
          setUpLoadingImage(true);

          const endpoint = await GetUploadEndpoint({
            filename: getUploadFilename(file.name),
          });

          if (isObject(endpoint) && endpoint.url) {
            const finalUrl = await UploadFileToProvider(
              {
                url: endpoint.url,
                fields: endpoint.fields,
                cdnHost: endpoint?.cdnHost,
              },
              file
            );

            SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

            if (finalUrl) {
              setWorkingSpeaker((prev) => ({ ...prev, image: finalUrl, original_image: finalUrl }));
            }
          }
        }
      } catch (error) {
        console.error(error);
        handleImageUploadErrors(error);
      } finally {
        setUpLoadingImage(false);
      }
    },
    [setWorkingSpeaker]
  );

  return (
    <UploadLarge
      editable
      cropModalId="upload-cast-image-cropper"
      ref={uploadInputRef}
      EditActionsPreHoverContent={() => (
        <Container
          onClick={() => uploadInputRef?.current?.click()}
          className="position-absolute top-0 left-0 cursor-pointer"
          height="200px"
          width="200px"
          style={{
            transform: 'translate(calc(-100% + 11px), -11px)',
          }}
        />
      )}
      uploading={upLoadingImage}
      style={{
        height: '200px',
        minHeight: '200px',
        maxHeight: '200px',
        width: '200px',
        minWidth: '200px',
        maxWidth: '200px',
        background: 'var(--secondary-violet-700)',
        border: workingSpeaker.image ? '1px solid var(--gray-600)' : '1px dashed var(--gray-600)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
      onDelete={() => setWorkingSpeaker((prev) => ({ ...prev, image: '' }))}
      displayImageUrl={workingSpeaker.image}
      hidePlaceholderImage
      hideIconIfImage
      standard
      onFile={handleImageFile}
      allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
      maxSizeInBytes={10 * 1024 * 1024} // 10MB
      maxSizeHeaderMessage="Cannot Upload File"
      CustomTitle={
        <Container width="100%" height="100%" className="flex-column ai-center">
          <Container width="100%" className="flex-1 flex ai-flex-end jc-center margin-b-12">
            <Text tag="h1" className="H1" opacity={initials ? 1 : 0.2}>
              {initials || 'AB'}
            </Text>
          </Container>

          <Container
            className="square-40 radius-full flex ai-center jc-center"
            margin="auto auto 16px auto"
            background={'var(--local-bttn-sec-transp-hover-bg)'}
          >
            <Icon name={ICONS.IMAGE_MEDIA_ADD} />
          </Container>
        </Container>
      }
    />
  );
});

export default UploadCastImage;
